import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useExportHighscores, useDeleteHighscores, useAllHighscores } from '@/hooks/useHighscores';
import { formatNumber, formatDate } from '@/lib/utils';

export function AdminPage() {
  const { logout } = useAuthStore();
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [csvContent, setCsvContent] = useState<string>('');

  const { data: scores, refetch } = useAllHighscores({ limit: 100 });
  const exportMutation = useExportHighscores();
  const deleteMutation = useDeleteHighscores();

  const handleExport = async () => {
    try {
      const result = await exportMutation.mutateAsync(selectedGame || undefined);
      setCsvContent(result.csv);
    } catch {
      alert('Export failed');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ukonnx_highscores_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = async () => {
    if (!confirm('Are you sure you want to delete all highscores? This cannot be undone.')) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(selectedGame || undefined);
      setCsvContent('');
      refetch();
      alert('Highscores deleted');
    } catch {
      alert('Delete failed');
    }
  };

  return (
    <div className="animate-fade-in max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-text-primary">Admin Panel</h1>
        <button onClick={logout} className="btn-ghost text-accent-red">
          Logout
        </button>
      </div>

      <div className="grid gap-6">
        {/* Export Section */}
        <div className="card p-6">
          <h2 className="font-semibold text-text-primary mb-4">Export Highscores</h2>
          
          <div className="flex flex-wrap gap-4 mb-4">
            <select
              value={selectedGame}
              onChange={(e) => setSelectedGame(e.target.value)}
              className="input max-w-xs"
            >
              <option value="">All Games</option>
              <option value="cabinet-transformer">Cabinet Transformer</option>
              <option value="automation-drop">Automation Drop</option>
            </select>

            <button 
              onClick={handleExport}
              disabled={exportMutation.isPending}
              className="btn-primary"
            >
              {exportMutation.isPending ? 'Exporting...' : 'Generate CSV'}
            </button>
          </div>

          {csvContent && (
            <>
              <textarea
                value={csvContent}
                readOnly
                className="input font-mono text-sm h-48 mb-4"
              />
              <div className="flex gap-4">
                <button onClick={handleDownload} className="btn-primary">
                  Download CSV
                </button>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(csvContent);
                    alert('Copied to clipboard');
                  }}
                  className="btn-secondary"
                >
                  Copy to Clipboard
                </button>
              </div>
            </>
          )}
        </div>

        {/* Danger Zone */}
        <div className="card p-6 border-accent-red/30">
          <h2 className="font-semibold text-accent-red mb-4">Danger Zone</h2>
          <p className="text-text-secondary text-sm mb-4">
            Delete all highscores. This action cannot be undone.
          </p>
          <button 
            onClick={handleClear}
            disabled={deleteMutation.isPending}
            className="btn bg-accent-red text-white hover:bg-red-600 px-6 py-3"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete All Highscores'}
          </button>
        </div>

        {/* Recent Scores */}
        <div className="card p-6">
          <h2 className="font-semibold text-text-primary mb-4">
            Recent Scores ({scores?.length || 0})
          </h2>
          
          {scores && scores.length > 0 ? (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-bg-deep sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-text-secondary">Name</th>
                    <th className="px-4 py-2 text-left text-text-secondary">Email</th>
                    <th className="px-4 py-2 text-left text-text-secondary">Game</th>
                    <th className="px-4 py-2 text-right text-text-secondary">Score</th>
                    <th className="px-4 py-2 text-right text-text-secondary">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-dark">
                  {scores.map((score) => (
                    <tr key={score.id} className="hover:bg-white/5">
                      <td className="px-4 py-2 text-text-primary">{score.player_name}</td>
                      <td className="px-4 py-2 text-text-muted">{score.email || '-'}</td>
                      <td className="px-4 py-2 text-text-secondary">{score.game_id}</td>
                      <td className="px-4 py-2 text-right font-medium text-text-primary">
                        {formatNumber(score.score)}
                      </td>
                      <td className="px-4 py-2 text-right text-text-muted">
                        {formatDate(score.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-text-muted">No scores yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
